// App.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ThreeScene from './components/ThreeScene';
import ControlPanel from './components/ControlPanel';
import { calculatePrice } from './pricing';
import './App.css';

// ============================================================
// SHOPIFY / BACKEND CONFIGURATION
// ============================================================
// Backend Vercel serverless function'ı configurator seçimini Shopify Admin API
// üzerinden gizli bir ürün/varyant'a çevirir ve frontend bunu standart
// /cart/add.js ile gerçek Shopify sepetine ekler.
const SHOPIFY_CONFIG = {
  // Configurator'ı barındıran Shopify mağaza URL'si (postMessage origin için)
  storeUrl: 'https://www.siriusdiamant.de',
  // Backend endpoint. Boş bırakılırsa configurator kendi origin'inden /api/... çağırır.
  backendUrl: process.env.REACT_APP_BACKEND_URL || '',
};

const ADD_TO_CART_ENDPOINT = `${SHOPIFY_CONFIG.backendUrl || ''}/api/add-to-cart`;

// Okunabilir etiketler
const STONE_TYPE_LABELS = {
  'RO': 'Round', 'EM': 'Emerald', 'OV': 'Oval',
  'MR': 'Marquise', 'PR': 'Princess', 'PE': 'Pear'
};
const METAL_COLOR_LABELS = {
  'YellowGold': 'Yellow Gold', 'WhiteGold': 'White Gold', 'RoseGold': 'Rose Gold'
};
const METAL_TYPE_LABELS = {
  'gold18k': '18K Gold', 'gold14k': '14K Gold', 'gold8k': '8K Gold', 'silver': 'Silver'
};
const STONE_ORIGIN_LABELS = {
  'natural': 'Natural', 'lab': 'Lab Grown'
};

function App() {

  document.title = "Rosy Ring Configurator";

  // Tüm ana seçimler başlangıçta boş → kullanıcı "Build Your Dream Ring" ile karşılanır
  // ve seçtiği parça 3D görünümde ADIM ADIM eklenir.
  const [bandType, setBandType] = useState(null);
  const [metalColor, setMetalColor] = useState(null);
  const [metalType, setMetalType] = useState(null);
  const [stoneType, setStoneType] = useState(null);
  const [stoneSize, setStoneSize] = useState(null);
  const [yuvaType, setYuvaType] = useState(null);
  const [stoneOrigin, setStoneOrigin] = useState('natural');
  const [ringSize, setRingSize] = useState(14);

  const [cartStatus, setCartStatus] = useState('idle'); // idle | loading | success | error
  const [cartError, setCartError] = useState('');

  const stoneSizeOptions = useMemo(() => ({
    'RO': [ { value: '1', text: '0.30 ct', sizeCode: '4343' }, { value: '2', text: '0.50 ct', sizeCode: '5252' }, { value: '3', text: '0.70 ct', sizeCode: '5757' }, { value: '4', text: '1.00 ct', sizeCode: '6565' }, { value: '5', text: '1.20 ct', sizeCode: '6868' } ],
    'EM': [ { value: '1', text: '0.30 ct', sizeCode: '5030' }, { value: '2', text: '0.50 ct', sizeCode: '6040' }, { value: '3', text: '0.70 ct', sizeCode: '6545' }, { value: '4', text: '1.00 ct', sizeCode: '7050' }, { value: '5', text: '1.20 ct', sizeCode: '8560' } ],
    'OV': [ { value: '1', text: '0.30 ct', sizeCode: '5035' }, { value: '2', text: '0.50 ct', sizeCode: '6545' }, { value: '3', text: '0.70 ct', sizeCode: '7555' }, { value: '4', text: '1.00 ct', sizeCode: '8060' }, { value: '5', text: '1.20 ct', sizeCode: '9065' } ],
    'MR': [ { value: '1', text: '0.30 ct', sizeCode: '6530' }, { value: '2', text: '0.50 ct', sizeCode: '8040' }, { value: '3', text: '0.70 ct', sizeCode: '9045' }, { value: '4', text: '1.00 ct', sizeCode: '1050' }, { value: '5', text: '1.20 ct', sizeCode: '1155' } ],
    'PR': [ { value: '1', text: '0.30 ct', sizeCode: '3535' }, { value: '2', text: '0.50 ct', sizeCode: '4343' }, { value: '3', text: '0.70 ct', sizeCode: '5050' }, { value: '4', text: '1.00 ct', sizeCode: '5555' }, { value: '5', text: '1.20 ct', sizeCode: '6060' } ],
    'PE': [ { value: '1', text: '0.30 ct', sizeCode: '6040' }, { value: '2', text: '0.50 ct', sizeCode: '7550' }, { value: '3', text: '0.70 ct', sizeCode: '8555' }, { value: '4', text: '1.00 ct', sizeCode: '9060' }, { value: '5', text: '1.20 ct', sizeCode: '1065' } ]
  }), []);

  const currentStoneSizeCode = stoneType && stoneSize
    ? (stoneSizeOptions[stoneType]?.find(o => o.value === stoneSize)?.sizeCode || '')
    : '';

  // 3D viewport'a geçecek flags:
  // her parça YALNIZCA ilgili seçim yapıldığında görünür
  const hasBand = !!bandType;
  const hasStone = !!(stoneType && stoneSize);
  const hasSetting = !!(stoneType && stoneSize && yuvaType);
  const hasAnything = hasBand || hasStone || hasSetting;
  const isFullyConfigured = hasBand && hasStone && hasSetting && !!metalColor && !!metalType;

  const threeJsProps = {
    stoneType,
    stoneSize,
    stoneCode: currentStoneSizeCode,
    yuvaType,
    yuvaMetal: metalColor || 'WhiteGold',
    bandType,
    bandMetal: metalColor || 'WhiteGold',
    // Adım-adım gösterim
    showBand: hasBand,
    showStone: hasStone,
    showSetting: hasSetting,
  };

  // Taş tipi değişince geçerli bir boyut seçili değilse sıfırla
  useEffect(() => {
    if (!stoneType) {
      setStoneSize(null);
      return;
    }
    const validSizes = stoneSizeOptions[stoneType]?.map((o) => o.value) || [];
    if (!stoneSize || !validSizes.includes(stoneSize)) {
      // Taş şekli değiştiğinde karat seçimi de kullanıcıya bırakılır (sıfırlanır)
      setStoneSize(null);
    }
  }, [stoneType, stoneSize, stoneSizeOptions]);

  // Taş şekli değiştiğinde yuva seçimini SIFIRLAMA — analog yuvaya otomatik çevir.
  // round/oval grubu (A*) ile diğerleri (S*) birebir eşleşiyor; sadece prefix farkı var.
  //   AH-NO ↔ SH-NO, AY-NO ↔ SY-NO, AY-ST ↔ SY-ST, AY-HL ↔ SY-HL,
  //   AK-NO ↔ SK-NO, AK-ST ↔ SK-ST
  useEffect(() => {
    if (!yuvaType || !stoneType) return;
    const isRoundOrOval = stoneType === 'RO' || stoneType === 'OV';
    const roundOvalYuvas = ['AH-NO', 'AY-NO', 'AY-ST', 'AY-HL', 'AK-NO', 'AK-ST'];
    const otherYuvas = ['SH-NO', 'SY-NO', 'SY-ST', 'SY-HL', 'SK-NO', 'SK-ST'];
    const allowed = isRoundOrOval ? roundOvalYuvas : otherYuvas;
    if (allowed.includes(yuvaType)) return;

    // Prefix swap ile muadilini bul
    const swapped = isRoundOrOval
      ? yuvaType.replace(/^S/, 'A')
      : yuvaType.replace(/^A/, 'S');

    if (allowed.includes(swapped)) {
      setYuvaType(swapped);
    } else {
      setYuvaType(null);
    }
  }, [stoneType, yuvaType]);

  // Fiyat: tam konfigüre edilmediyse null dönsün
  const currentPrice = isFullyConfigured
    ? calculatePrice(stoneType, stoneSize, yuvaType, bandType, metalType, stoneOrigin)
    : null;

  // Fiyatı formatla (EUR olarak)
  const formatPrice = useCallback((amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }, []);

  const formattedPrice = currentPrice !== null ? formatPrice(currentPrice) : '—';

  // Belirli bir seçenek değiştiğinde fiyat farkını hesapla.
  // Eksik seçim varsa fark gösterme.
  const getPriceDiff = useCallback((category, value) => {
    if (!isFullyConfigured) return 0;
    let newPrice;
    switch (category) {
      case 'stoneType':
        newPrice = calculatePrice(value, stoneSize, yuvaType, bandType, metalType, stoneOrigin);
        break;
      case 'stoneSize':
        newPrice = calculatePrice(stoneType, value, yuvaType, bandType, metalType, stoneOrigin);
        break;
      case 'stoneOrigin':
        newPrice = calculatePrice(stoneType, stoneSize, yuvaType, bandType, metalType, value);
        break;
      case 'yuvaType':
        newPrice = calculatePrice(stoneType, stoneSize, value, bandType, metalType, stoneOrigin);
        break;
      case 'bandType':
        newPrice = calculatePrice(stoneType, stoneSize, yuvaType, value, metalType, stoneOrigin);
        break;
      case 'metalType':
        newPrice = calculatePrice(stoneType, stoneSize, yuvaType, bandType, value, stoneOrigin);
        break;
      default:
        return 0;
    }
    return newPrice - currentPrice;
  }, [stoneType, stoneSize, stoneOrigin, yuvaType, bandType, metalType, currentPrice, isFullyConfigured]);

  // Seçimlerin okunabilir özetini oluştur — seçilmemiş alanlar "—" gösterir
  const getOrderSummary = useCallback(() => {
    const selectedStoneSizeText = stoneType && stoneSize
      ? (stoneSizeOptions[stoneType]?.find((option) => option.value === stoneSize)?.text || stoneSize)
      : '—';

    return {
      'Band Style': bandType ? `Band ${bandType}` : '—',
      'Metal Color': metalColor ? (METAL_COLOR_LABELS[metalColor] || metalColor) : '—',
      'Metal Type': metalType ? (METAL_TYPE_LABELS[metalType] || metalType) : '—',
      'Stone Type': stoneType ? (STONE_TYPE_LABELS[stoneType] || stoneType) : '—',
      'Stone Size': selectedStoneSizeText,
      'Setting Type': yuvaType || '—',
      'Stone Origin': STONE_ORIGIN_LABELS[stoneOrigin] || stoneOrigin,
      'Ring Size': String(ringSize),
      'Price': formattedPrice,
    };
  }, [stoneType, stoneSize, stoneOrigin, yuvaType, metalType, metalColor, bandType, ringSize, stoneSizeOptions, formattedPrice]);

  const handleBuy = useCallback(async () => {
    if (!isFullyConfigured || currentPrice === null) {
      setCartStatus('error');
      setCartError('Please complete your ring before adding it to cart.');
      setTimeout(() => setCartStatus('idle'), 4000);
      return;
    }

    setCartStatus('loading');
    setCartError('');

    // Capture a screenshot from the main 3D viewport for the product image
    let imageBase64 = null;
    try {
      const canvas = document.querySelector('#view-3d canvas');
      if (canvas) {
        const size = 800;
        const offscreen = document.createElement('canvas');
        offscreen.width = size;
        offscreen.height = size;
        const ctx = offscreen.getContext('2d');
        // Draw the 3D canvas scaled to 800x800
        ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, size, size);
        // JPEG at 85% quality keeps the file small (~100-200KB)
        const dataUrl = offscreen.toDataURL('image/jpeg', 0.85);
        imageBase64 = dataUrl.split(',')[1]; // strip "data:image/jpeg;base64,"
      }
    } catch (e) {
      console.warn('Could not capture canvas screenshot:', e);
    }

    const payload = {
      stoneType,
      stoneSize,
      settingType: yuvaType,
      bandType,
      metalType,
      metalColor,
      stoneOrigin,
      ringSize: String(ringSize),
      image: imageBase64,
    };

    try {
      const response = await fetch(ADD_TO_CART_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || `HTTP ${response.status}`);
      }

      const { variantId, properties } = await response.json();
      if (!variantId) {
        throw new Error('Variant ID missing from server response.');
      }

      setCartStatus('success');
      setTimeout(() => setCartStatus('idle'), 3000);

      const message = {
        type: 'ADD_TO_CART',
        variantId: String(variantId),
        quantity: 1,
        properties: properties || {},
      };

      if (window.parent !== window) {
        window.parent.postMessage(message, SHOPIFY_CONFIG.storeUrl);
      } else {
        // Standalone fallback: hit /cart/add.js on the same origin (only
        // works when the configurator is served from the Shopify domain).
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: Number(variantId),
                  quantity: 1,
                  properties: properties || {},
                },
              ],
            }),
          });
          window.top.location.href = '/cart';
        } catch (e) {
          console.warn('Standalone /cart/add.js fallback failed:', e);
        }
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      setCartStatus('error');
      setCartError('Could not add to basket. Please try again.');
      setTimeout(() => setCartStatus('idle'), 4000);
    }
  }, [
    bandType,
    currentPrice,
    isFullyConfigured,
    metalColor,
    metalType,
    ringSize,
    stoneOrigin,
    stoneSize,
    stoneType,
    yuvaType,
  ]);

  return (
    <div className="App">
      <div id="view-container">
        {/* Hiçbir şey seçilmediğinde tüm viewport'u kaplayan davet ekranı */}
        {!hasAnything && (
          <div className="placeholder-overlay">
            <div className="placeholder-inner">
              <div className="placeholder-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <circle cx="12" cy="14" r="6"/>
                  <path d="M8 8l4-5 4 5"/>
                </svg>
              </div>
              <h2>Build Your Dream Ring</h2>
              <p className="placeholder-subtitle">Every detail, crafted by you.</p>
              <div className="placeholder-steps">
                <span>1. Band</span>
                <span className="sep">→</span>
                <span>2. Metal</span>
                <span className="sep">→</span>
                <span>3. Stone</span>
                <span className="sep">→</span>
                <span>4. Setting</span>
              </div>
              <p className="placeholder-cta">Start by choosing a <strong>Band Style</strong> on the right.</p>
            </div>
          </div>
        )}

        <div className="viewport" id="view-3d">
          <div className="viewport-label">3D VIEW</div>
          {hasAnything && (
            <div className="rotate-hint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.92-5.27l-3.26-1.5"/>
              </svg>
              <span>Drag to rotate</span>
            </div>
          )}
          <ThreeScene cameraType="3D" {...threeJsProps} />
        </div>
        <div className="viewport" id="view-x">
          <div className="viewport-label">SIDE</div>
          <ThreeScene cameraType="X" {...threeJsProps} />
        </div>
        <div className="viewport" id="view-y">
          <div className="viewport-label">TOP</div>
          <ThreeScene cameraType="Y" {...threeJsProps} />
        </div>
        <div className="viewport" id="view-z">
          <div className="viewport-label">FRONT</div>
          <ThreeScene cameraType="Z" {...threeJsProps} />
        </div>
      </div>

      <ControlPanel
        stoneType={stoneType}
        setStoneType={setStoneType}
        stoneSize={stoneSize}
        setStoneSize={setStoneSize}
        stoneSizeOptions={stoneSizeOptions}
        stoneOrigin={stoneOrigin}
        setStoneOrigin={setStoneOrigin}
        yuvaType={yuvaType}
        setYuvaType={setYuvaType}
        metalColor={metalColor}
        setMetalColor={setMetalColor}
        metalType={metalType}
        setMetalType={setMetalType}
        bandType={bandType}
        setBandType={setBandType}
        ringSize={ringSize}
        setRingSize={setRingSize}
        onBuy={handleBuy}
        cartStatus={cartStatus}
        cartError={cartError}
        orderSummary={getOrderSummary()}
        currentPrice={formattedPrice}
        isFullyConfigured={isFullyConfigured}
        getPriceDiff={getPriceDiff}
        formatPrice={formatPrice}
      />
    </div>
  );
}






export default App;
